import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, IconButton, InputBase, Typography, Fade, Slide, CircularProgress } from "@mui/material";
import {
  Close as CloseIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import useApp from "../../../store/contexts/AppContext";

// Styled Components
const SearchContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  height: "76px",
  minHeight: "76px",
  maxHeight: "76px",
  overflow: "hidden",
  background: theme.palette.mode === "dark"
    ? alpha("#1a1f2e", 0.98)
    : alpha("#ffffff", 0.98),
  borderBottom: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.08)
      : alpha("#000", 0.08)
  }`,
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  gap: "8px",
  position: "relative",
  zIndex: 15,
  flexShrink: 0,
  boxSizing: "border-box",
  animation: "slideDown 0.25s ease-out",

  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-100%)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: "14px",
  fontWeight: 400,
  color: theme.palette.mode === "dark" ? "#fff" : "#1a1a1a",
  background: theme.palette.mode === "dark"
    ? alpha("#fff", 0.06)
    : alpha("#000", 0.05),
  borderRadius: "20px",
  border: `1px solid ${theme.palette.mode === "dark"
    ? alpha("#fff", 0.1)
    : alpha("#000", 0.12)}`,
  transition: "all 0.2s ease",
  paddingLeft: "14px",

  "&.Mui-focused": {
    background: theme.palette.mode === "dark"
      ? alpha("#fff", 0.08)
      : alpha("#000", 0.06),
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
  },

  "& input": {
    padding: "11px 0",
    boxSizing: "border-box",
    color: theme.palette.mode === "dark" ? "#fff" : "#1a1a1a",

    "&::placeholder": {
      color: theme.palette.mode === "dark"
        ? alpha("#fff", 0.4)
        : alpha("#000", 0.4),
    },
  },
}));

const ResultCounter = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  color: theme.palette.mode === "dark"
    ? alpha("#fff", 0.7)
    : alpha("#000", 0.7),
  minWidth: "50px",
  textAlign: "center",
  padding: "4px 10px",
  borderRadius: "6px",
  background: theme.palette.mode === "dark"
    ? alpha(theme.palette.primary.main, 0.15)
    : alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  flexShrink: 0,
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? alpha("#fff", 0.9)
    : alpha("#000", 0.8),
  padding: "7px",
  transition: "all 0.2s ease",
  background: theme.palette.mode === "dark"
    ? alpha("#fff", 0.05)
    : alpha("#000", 0.05),
  borderRadius: "6px",

  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
    transform: "scale(1.05)",
  },

  "&:disabled": {
    opacity: 0.3,
    color: theme.palette.mode === "dark"
      ? alpha("#fff", 0.3)
      : alpha("#000", 0.3),
  },

  "& .MuiSvgIcon-root": {
    fontSize: "21px",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? alpha("#fff", 0.95)
    : alpha("#000", 0.9),
  padding: "8px",
  transition: "all 0.2s ease",
  background: theme.palette.mode === "dark"
    ? alpha("#fff", 0.12)
    : alpha("#000", 0.1),
  border: `1px solid ${theme.palette.mode === "dark"
    ? alpha("#fff", 0.15)
    : alpha("#000", 0.15)}`,
  borderRadius: "8px",
  flexShrink: 0,

  "&:hover": {
    background: alpha(theme.palette.error.main, 0.2),
    color: theme.palette.error.main,
    transform: "scale(1.1)",
    borderColor: theme.palette.error.main,
  },

  "& .MuiSvgIcon-root": {
    fontSize: "22px",
    fontWeight: "bold",
  },
}));

export default function ChatSearch({
  messages = [],
  onClose,
  onResultSelect,
  searchQuery: externalSearchQuery = "",
  onSearchChange,
  currentIndex = 0,
  onSearchResults
}) {
  const { searchRoomMessagesApiCall } = useApp();

  const [internalSearchQuery, setInternalSearchQuery] = useState(externalSearchQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [isSearchingBackend, setIsSearchingBackend] = useState(false);
  const [backendResults, setBackendResults] = useState([]);
  const searchInputRef = useRef(null);
  const backendSearchTimeoutRef = useRef(null);

  // Use external search query if provided, otherwise use internal
  const searchQuery = onSearchChange ? externalSearchQuery : internalSearchQuery;
  const setSearchQuery = onSearchChange || setInternalSearchQuery;

  // Auto-focus and select search input when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      const input = searchInputRef.current?.querySelector('input');
      if (input) {
        input.focus();
        input.select();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  // Search through messages - local first, then backend
  const prevSearchQueryRef = useRef("");
  const isFirstSearchRef = useRef(true);

  useEffect(() => {
    // Clear backend search timeout on query change
    if (backendSearchTimeoutRef.current) {
      clearTimeout(backendSearchTimeoutRef.current);
    }

    if (searchQuery.trim() === "") {
      setSearchResults([]);
      setBackendResults([]);
      setCurrentResultIndex(0);
      setIsSearchingBackend(false);
      if (onSearchResults) onSearchResults([]);
      prevSearchQueryRef.current = "";
      isFirstSearchRef.current = true;
      return;
    }

    const query = searchQuery.toLowerCase();

    // Step 1: Search local messages FIRST (instant)
    const localResults = messages
      .map((msg, index) => ({
        ...msg,
        originalIndex: index,
        source: 'local', // Mark as local
      }))
      .filter((msg) => msg.message?.toLowerCase().includes(query));

    setSearchResults(localResults);
    if (onSearchResults) onSearchResults(localResults);

    // Only reset index when the actual search query changes
    if (prevSearchQueryRef.current !== searchQuery) {
      prevSearchQueryRef.current = searchQuery;
      setCurrentResultIndex(0);

      // Highlight first result only on new search
      if (localResults.length > 0 && onResultSelect && isFirstSearchRef.current) {
        setTimeout(() => {
          onResultSelect(localResults[0], 0);
          isFirstSearchRef.current = false;
        }, 100);
      }

      // Step 2: Search backend in background (after 500ms delay to avoid too many requests)
      backendSearchTimeoutRef.current = setTimeout(async () => {
        setIsSearchingBackend(true);

        try {
          const result = await searchRoomMessagesApiCall(searchQuery, 1, 50);

          if (result?.success && result?.data?.messages) {
            const backendMessages = result.data.messages;

            // Get IDs of local messages to avoid duplicates
            const localMessageIds = new Set(messages.map(m => m.id));

            // Filter out messages already in local results
            const newBackendResults = backendMessages
              .filter(msg => !localMessageIds.has(msg.id))
              .map(msg => ({
                ...msg,
                source: 'backend', // Mark as from backend
              }));

            setBackendResults(newBackendResults);

            // Merge local and backend results
            const allResults = [...localResults, ...newBackendResults];
            setSearchResults(allResults);
            if (onSearchResults) onSearchResults(allResults);
          }
        } catch (error) {
          console.error('Backend search error:', error);
        } finally {
          setIsSearchingBackend(false);
        }
      }, 500);
    }

    return () => {
      if (backendSearchTimeoutRef.current) {
        clearTimeout(backendSearchTimeoutRef.current);
      }
    };
  }, [searchQuery, messages, onSearchResults, onResultSelect, searchRoomMessagesApiCall]);

  const handlePrevious = useCallback(() => {
    if (searchResults.length === 0) return;

    const newIndex = currentResultIndex > 0
      ? currentResultIndex - 1
      : searchResults.length - 1;

    setCurrentResultIndex(newIndex);

    if (onResultSelect && searchResults[newIndex]) {
      onResultSelect(searchResults[newIndex], newIndex);
    }
  }, [searchResults, onResultSelect, currentResultIndex]);

  const handleNext = useCallback(() => {
    if (searchResults.length === 0) return;

    const newIndex = currentResultIndex < searchResults.length - 1
      ? currentResultIndex + 1
      : 0;

    setCurrentResultIndex(newIndex);

    if (onResultSelect && searchResults[newIndex]) {
      onResultSelect(searchResults[newIndex], newIndex);
    }
  }, [searchResults, onResultSelect, currentResultIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
        if (onClose) {
          onClose();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (e.shiftKey) {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    // Add listener directly to window
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, handleNext, handlePrevious]);

  return (
    <Slide direction="down" in timeout={300}>
      <SearchContainer>
        <SearchInput
          ref={searchInputRef}
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          inputProps={{ 'aria-label': 'Search messages' }}
          endAdornment={
            searchQuery ? (
              <IconButton
                size="small"
                onClick={() => setSearchQuery("")}
                sx={(theme) => ({
                  padding: "5px",
                  marginRight: "6px",
                  flexShrink: 0,
                  color: theme.palette.mode === "dark" ? alpha("#fff", 0.45) : alpha("#000", 0.35),
                  borderRadius: "50%",
                  "&:hover": {
                    color: theme.palette.error.main,
                    background: alpha(theme.palette.error.main, 0.1),
                  },
                })}
              >
                <CloseIcon sx={{ fontSize: "15px" }} />
              </IconButton>
            ) : null
          }
        />

        {searchQuery && (
          <Fade in timeout={200}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isSearchingBackend && (
                <CircularProgress
                  size={16}
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    opacity: 0.7
                  })}
                />
              )}
              <ResultCounter>
                {searchResults.length > 0
                  ? `${currentResultIndex + 1}/${searchResults.length}${isSearchingBackend ? '...' : ''}`
                  : isSearchingBackend ? "Searching..." : "0/0"}
              </ResultCounter>
            </Box>
          </Fade>
        )}

        <NavigationButton
          onClick={handlePrevious}
          disabled={searchResults.length === 0}
          aria-label="Previous result"
        >
          <ArrowUpIcon />
        </NavigationButton>

        <NavigationButton
          onClick={handleNext}
          disabled={searchResults.length === 0}
          aria-label="Next result"
        >
          <ArrowDownIcon />
        </NavigationButton>

        <CloseButton onClick={onClose} aria-label="Close search">
          <CloseIcon />
        </CloseButton>
      </SearchContainer>
    </Slide>
  );
}
